<script setup>
import {useConfigurateurStore} from "../../stores/configurateur";
import {ref, computed, nextTick} from 'vue';
import Logger from "../../utils/logger";

const logger = new Logger('configurateur')

const store = useConfigurateurStore();
const isCollapsed = ref(false);
const showModal = ref(false);
const selectedStageForModal = ref(null);
const activeTab = ref('pilotage'); // 'pilotage' or 'bapteme'
const ids = ref([])

const handleClickDecrementTour = (stage) => {
    if (currentFormIds(stage.form.id).nb_tour > stage.nb_tour_min) {
        currentFormIds(stage.form.id).nb_tour -= 1;
    }
}

const handleClickIncrementTour = (stage) => {
    if (currentFormIds(stage.form.id).nb_tour < stage.nb_tour_max) {
        currentFormIds(stage.form.id).nb_tour += 1;
    }
}

const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
}

const showStageInfo = (stage, event) => {
    logger.log('Icône info cliquée pour stage:', stage);
    selectedStageForModal.value = stage;
    showModal.value = true;
}

const closeModal = () => {
    showModal.value = false;
    selectedStageForModal.value = null;
}

const toggleMode = (stage, newMode) => {
    // Find voiture_id in the stage structure - it might be in different places
    const voitureId = stage.voiture_id || stage.form?.voiture_id || stage.form?.id;

    logger.log('🔧 Toggling mode for car:', voitureId, 'to:', newMode);
    logger.log('🔧 Stage structure:', {
        voiture_id: stage.voiture_id,
        form_voiture_id: stage.form?.voiture_id,
        form_id: stage.form?.id,
        stage_keys: Object.keys(stage)
    });

    if (!voitureId) {
        console.error('🚨 Unable to find voiture_id in stage:', stage);
        return;
    }

    // Use the store action to toggle the car experience mode
    store.toggleCarExperienceMode(voitureId, newMode);
}

const getCurrentStagePrice = (stage) => {
    // Use the store getter for dynamic pricing based on date/slot selection
    const dynamicPrice = store.getComplementStagePrice(stage);

    if (!dynamicPrice) {
        // Fallback to form data if no pricing structure is available
        const currentForm = stage.form;
        return {
            montant_ttc: currentForm.montant_ttc,
            old_montant_ttc: currentForm.old_montant_ttc,
            nb_tour_min: currentForm.nb_tour_min,
            montant_ttc_par_tour: currentForm.montant_ttc_par_tour,
            old_montant_ttc_par_tour: currentForm.old_montant_ttc_par_tour,
            rule: currentForm.rule,
            promo: currentForm.promo
        };
    }

    return dynamicPrice;
}

// Switch active tab
const switchTab = (tab) => {
    activeTab.value = tab;
}

// Get stages filtered by active tab mode
const stagesForActiveTab = computed(() => {
    if (!store.autreStagesFiltered) return [];

    const filteredStages = [];
    const seenVoitures = new Set();
    const targetMode = activeTab.value === 'bapteme';

    store.autreStagesFiltered.forEach((stage, index) => {
        const voitureId = stage.voiture_id || stage.form?.voiture_id;
        let stageToAdd = null;

        if (stage.dual_mode && stage.stages && stage.forms) {
            // For dual-mode cars, get the stage data for the active tab
            const modeStage = stage.stages[activeTab.value];
            const modeForm = stage.forms[activeTab.value];

            if (modeStage && modeForm) {
                // Synchroniser le prix du form avec celui affiché (getCurrentStagePrice)
                const displayPrice = getCurrentStagePrice({
                    ...modeStage,
                    form: modeForm,
                    dual_mode: stage.dual_mode,
                    stages: stage.stages,
                    voiture_title: stage.voiture_title
                });

                // Corriger le form avec le prix d'affichage
                const correctedForm = {
                    ...modeForm,
                    montant_ttc: displayPrice.montant_ttc,
                    old_montant_ttc: displayPrice.old_montant_ttc,
                    montant_ttc_par_tour: displayPrice.montant_ttc_par_tour,
                    old_montant_ttc_par_tour: displayPrice.old_montant_ttc_par_tour,
                    rule: displayPrice.rule,
                    promo: displayPrice.promo,
                    nb_tour_min: displayPrice.nb_tour_min
                };

                stageToAdd = {
                    ...modeStage,
                    ...{
                        form: correctedForm,
                        voiture_id: voitureId,
                        voiture_title: stage.voiture_title,
                        voiture_image: stage.voiture_image,
                        position: stage.position,
                        is_current_car: stage.is_current_car,
                        tab_mode: activeTab.value,
                        dual_mode: false
                    }
                };
            }
        } else {
            // For single-mode cars, check if they match the active tab
            if (stage.form && stage.form.is_bapteme === targetMode) {
                stageToAdd = {
                    ...stage,
                    voiture_id: voitureId,
                    tab_mode: activeTab.value,
                    dual_mode: false
                };
            }
        }

        // Add the stage if we found a match and haven't seen this car yet
        if (stageToAdd && !seenVoitures.has(voitureId)) {
            filteredStages.push(stageToAdd);
            seenVoitures.add(voitureId);
        }
    });

    // Sort by position, with current car first if in bapteme tab
    return filteredStages.sort((a, b) => {
        if (activeTab.value === 'bapteme') {
            if (a.is_current_car && !b.is_current_car) return -1;
            if (!a.is_current_car && b.is_current_car) return 1;
        }
        return a.position - b.position;
    });
});

// Count stages per tab for display
const pilotageCount = computed(() => {
    if (!store.autreStagesFiltered) return 0;
    const uniqueVoitures = new Set();
    store.autreStagesFiltered.forEach(stage => {
        const voitureId = stage.voiture_id;
        if ((stage.dual_mode && stage.stages?.pilotage) || (!stage.dual_mode && !stage.form.is_bapteme)) {
            uniqueVoitures.add(voitureId);
        }
    });
    return uniqueVoitures.size;
});

const baptemeCount = computed(() => {
    if (!store.autreStagesFiltered) return 0;
    const uniqueVoitures = new Set();
    store.autreStagesFiltered.forEach(stage => {
        const voitureId = stage.voiture_id;
        if ((stage.dual_mode && stage.stages?.bapteme) || (!stage.dual_mode && stage.form.is_bapteme)) {
            uniqueVoitures.add(voitureId);
        }
    });
    return uniqueVoitures.size;
});

// Count selected stages per mode
const selectedPilotageCount = computed(() => {
    if (!store.stage_ids || !store.autreStagesFiltered) return 0;
    let count = 0;

    store.stage_ids.forEach(selectedStageForm => {
        if (selectedStageForm && selectedStageForm.is_bapteme === false) {
            count++;
        }
    });

    return count;
});

const selectedBaptemeCount = computed(() => {
    if (!store.stage_ids || !store.autreStagesFiltered) return 0;
    let count = 0;

    store.stage_ids.forEach(selectedStageForm => {
        if (selectedStageForm && selectedStageForm.is_bapteme === true) {
            count++;
        }
    });

    return count;
});

const currentFormIds = (stageId) => {
    return store.stage_ids.find(form => form.id === stageId);
}

</script>

<template>
    <section id="step-complement">
        <div class="card-step-group shadow-sm is-sticky">
            <div class="step-title d-flex justify-content-between align-items-center">
                <div class="circle"
                     :class="{ 'active': store.getStepNumber('complement') == store.step, 'completed': store.isCompleted(store.getStepNumber('complement')) }">
                    {{ store.getStepNumber('complement') }}
                </div>
                <span class="h5 large fw-bolder mb-2">{{ $t('Voiture complémentaire') }}</span>
                <button class="btn btn-sm" type="button" @click="toggleCollapse">
                    <i class="bi" :class="isCollapsed ? 'bi-plus-circle' : 'bi-dash-circle'"></i>
                </button>
            </div>
            <div id="collapse-complement" class="collapse-content" :class="{ 'collapsed': isCollapsed }">
                <div class="card card-step">
                    <div class="card-title">
                        <p class="m-0 fw-bolder">{{ $t('Sélectionnez des voitures puis le nombre de tours.') }}</p>
                    </div>

                    <div class="card-body">
                        <div class="row">
                            <div id="list-voitures" class="col-md-12">
                                <!-- Message si aucune voiture complémentaire disponible -->
                                <div v-if="!store.autreStagesFiltered || store.autreStagesFiltered.length === 0"
                                     class="text-center py-4">
                                    <div class="alert alert-info border-0" role="alert">
                                        <i class="bi bi-info-circle me-2"></i>
                                        {{
                                            $t('Aucune voiture complémentaire n\'est disponible pour cette sélection.')
                                        }}
                                    </div>
                                </div>

                                <!-- Onglets Pilotage / Baptême -->
                                <div v-else>
                                    <div class="tab-navigation mb-3">
                                        <ul class="nav nav-tabs" role="tablist">
                                            <li class="nav-item" role="presentation">
                                                <button
                                                    @click="switchTab('pilotage')"
                                                    :class="['nav-link', { active: activeTab === 'pilotage' }]"
                                                    type="button" role="tab">
                                                    <i class="bi bi-steering-wheel me-2"></i>
                                                    {{ $t('Pilotage') }}
                                                    <span v-if="selectedPilotageCount > 0" class="badge ms-2"
                                                          :class="activeTab === 'pilotage' ? 'badge-light' : 'badge-secondary'">
                                                        {{ selectedPilotageCount }}
                                                    </span>
                                                </button>
                                            </li>
                                            <li class="nav-item" role="presentation">
                                                <button
                                                    @click="switchTab('bapteme')"
                                                    :class="['nav-link', { active: activeTab === 'bapteme' }]"
                                                    type="button" role="tab">
                                                    <i class="bi bi-person-heart me-2"></i>
                                                    {{ $t('Baptême') }}
                                                    <span v-if="selectedBaptemeCount > 0" class="badge ms-2"
                                                          :class="activeTab === 'bapteme' ? 'badge-light' : 'badge-secondary'">
                                                        {{ selectedBaptemeCount }}
                                                    </span>
                                                </button>
                                            </li>
                                        </ul>
                                    </div>

                                    <!-- Contenu de l'onglet actif -->
                                    <div class="tab-content">
                                        <div class="tab-pane fade show active">
                                            <!-- Message si pas de voitures pour cet onglet -->
                                            <div v-if="stagesForActiveTab.length === 0" class="text-center py-4">
                                                <div class="alert alert-info border-0" role="alert">
                                                    <i class="bi bi-info-circle me-2"></i>
                                                    {{
                                                        activeTab === 'pilotage' ?
                                                            $t('Aucune voiture en mode Pilotage n\'est disponible.') :
                                                            $t('Aucune voiture en mode Baptême n\'est disponible.')
                                                    }}
                                                </div>
                                            </div>

                                            <!-- Liste des voitures pour l'onglet actif -->
                                            <div v-else class="row">
                                                <div v-for="(stage, index) in stagesForActiveTab"
                                                     :key="activeTab + '-' + stage.voiture_id + '-' + index"
                                                     class="col-12 p-2">
                                                    <div class="card card-config card-config-long card-voiture-config"
                                                         :class="{ 'current-car-highlight': stage.is_current_car }">

                                                        <!-- Current car indicator -->
                                                        <div v-if="stage.is_current_car" class="current-car-indicator">
                                                            <i class="bi bi-star-fill me-1"></i>
                                                            <span class="fw-bold">
                                                                {{
                                                                    activeTab === 'bapteme' ?
                                                                        $t('Votre voiture en mode Baptême') :
                                                                        $t('Votre voiture en mode Pilotage')
                                                                }}
                                                            </span>
                                                        </div>

                                                        <div
                                                            class="form-check form-switch d-flex align-items-center justify-content-center">
                                                            <input :value="currentFormIds(stage.form.id) || stage.form"
                                                                   v-model="store.stage_ids"
                                                                   :id="'car-product-comp-' + activeTab + '-' + index"
                                                                   class="form-check-input ms-3"
                                                                   type="checkbox" name="car-product-comp">
                                                        </div>

                                                        <div class="card-clickable-area">
                                                            <i class="bi bi-info-circle-fill show-infos"
                                                               @click.stop.prevent="showStageInfo(stage)"
                                                               title="Voir les détails du stage"></i>
                                                            <img :src="stage.voiture_image" alt=""
                                                                 class="card-config-bg">
                                                            <div class="card-body">
                                                                <div class="card-text">
                                                                    <span class="fw-bold">{{
                                                                            stage.voiture_title
                                                                        }}</span>
                                                                    <!-- Mode badge - static for current tab -->
                                                                    <div class="mode-indicator mt-1">
                                                                        <span
                                                                            :class="['badge', activeTab === 'bapteme' ? 'badge-bapteme' : 'badge-pilotage']">
                                                                            <i :class="activeTab === 'bapteme' ? 'bi bi-person-heart' : 'bi bi-steering-wheel'"></i>
                                                                            {{
                                                                                activeTab === 'bapteme' ? $t('Baptême') : $t('Pilotage')
                                                                            }}
                                                                        </span>
                                                                    </div>
                                                                    <div v-if="currentFormIds(stage.form.id)"
                                                                         class="d-flex align-items-center step-6-tour-selector">
                                                                        <button
                                                                            @click.prevent="handleClickDecrementTour(stage)"
                                                                            class="btn btn-icon btn-sm fs-6"
                                                                            type="button"
                                                                            :disabled="currentFormIds(stage.form.id).nb_tour == stage.nb_tour_min">
                                                                            <i class="bi bi-dash-circle-fill"></i>
                                                                        </button>
                                                                        <input
                                                                            v-model="currentFormIds(stage.form.id).nb_tour"
                                                                            class="form-control-plaintext fw-bold text-center"
                                                                            style="width: 2rem;"
                                                                            type="text" disabled="disabled">
                                                                        <button
                                                                            @click.prevent="handleClickIncrementTour(stage)"
                                                                            class="btn btn-icon btn-sm fs-6"
                                                                            type="button"
                                                                            :disabled="currentFormIds(stage.form.id).nb_tour == stage.nb_tour_max">
                                                                            <i class="bi bi-plus-circle-fill"></i>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                                <div class="card-price">
                                                                    <div
                                                                        class="price d-flex flex-row align-items-baseline">
                                                                        <span class="small fw-normal m-0 me-1">{{
                                                                                $t('À partir de')
                                                                            }}</span>
                                                                        <span
                                                                            v-if="getCurrentStagePrice(stage).old_montant_ttc && getCurrentStagePrice(stage).old_montant_ttc !== getCurrentStagePrice(stage).montant_ttc"
                                                                            class="old-price fw-normal text-decoration-line-through small fs-xxl-6 me-2">{{
                                                                                getCurrentStagePrice(stage).old_montant_ttc
                                                                            }}€</span>
                                                                        <span
                                                                            class="price fw-bolder text-info small fs-xl-6 fs-xxl-5 p-lg-0 p-xl-0">{{
                                                                                getCurrentStagePrice(stage).montant_ttc
                                                                            }}€</span>
                                                                        <span class="nb fw-normal small">/{{
                                                                                getCurrentStagePrice(stage).nb_tour_min
                                                                            }} tours</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modal pour les informations du stage complémentaire -->
            <div v-if="showModal" class="step-modal-overlay" @click="closeModal">
                <div class="step-modal-content" @click.stop>
                    <div class="step-modal-header">
                        <h3 class="step-modal-title">{{ selectedStageForModal?.voiture_title }}</h3>
                        <button type="button" class="btn-close" @click="closeModal">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>
                    <div class="step-modal-body">
                        <div v-if="selectedStageForModal" class="card-stage-infos">
                            <div class="card-text">
                                <div class="mb-3">
                                    <img :src="selectedStageForModal.voiture_image" alt="" class="img-fluid mb-3"
                                         style="max-height: 200px; width: 100%; object-fit: contain;">
                                </div>
                                <div class="stage-details">
                                    <p><strong>Désignation :</strong> {{ selectedStageForModal.designation }}</p>
                                    <div class="pricing-info mb-3">
                                        <p><strong>Tarification :</strong></p>
                                        <div class="d-flex align-items-baseline">
                                            <span
                                                v-if="selectedStageForModal.old_montant_ttc && selectedStageForModal.old_montant_ttc !== selectedStageForModal.montant_ttc"
                                                class="text-decoration-line-through me-2 text-muted">{{
                                                    selectedStageForModal.old_montant_ttc
                                                }}€</span>
                                            <span class="fs-5 fw-bolder text-info">{{
                                                    selectedStageForModal.montant_ttc
                                                }}€</span>
                                            <span class="ms-1 text-muted">/ {{ selectedStageForModal.nb_tour_min }} tours</span>
                                        </div>
                                    </div>
                                    <div class="tour-info">
                                        <p><strong>Nombre de tours :</strong></p>
                                        <ul class="list-unstyled">
                                            <li>• Minimum : {{ selectedStageForModal.nb_tour_min }} tours</li>
                                            <li>• Maximum : {{ selectedStageForModal.nb_tour_max }} tours</li>
                                            <li>• Actuellement sélectionné : {{
                                                    selectedStageForModal.form?.nb_tour || selectedStageForModal.nb_tour_min
                                                }} tours
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
/* #136 : le checkbox de sélection doit rester cliquable au-dessus des éléments
   superposés de la carte (indicateur "voiture courante" z-index:10, badges mode
   et sélecteur de tours z-index:11) qui passaient par-dessus en mode baptême. */
.form-check.form-switch {
    position: relative;
    z-index: 12;
}

.collapse-content {
    overflow: hidden;
    transition: max-height 0.3s ease-out;
    max-height: 2000px;
    /* Valeur suffisamment grande pour le contenu */
}

.collapse-content.collapsed {
    max-height: 0;
}

.mode-selectors {
    position: absolute;
    top: 30px;
    right: 5px;
}

/* Mode toggle section styling */
.mode-toggle-section {
    border: 1px solid #e9ecef;
    background-color: #f8f9fa !important;
}

/* Mode indicator badges */
.badge-bapteme {
    background-color: #e3f2fd;
    color: #1976d2;
    border: 1px solid #bbdefb;
}

.badge-pilotage {
    background-color: #fff3e0;
    color: #f57c00;
    border: 1px solid #ffcc02;
}

.mode-selectors .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
    transition: all 0.2s ease;
    position: relative;
}

/* Clickable badge selector states */
.badge-selector {
    cursor: pointer;
    user-select: none;
}

.badge-selector:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Selected state - more opaque and prominent */
.badge-selected {
    opacity: 1;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    z-index: 9 !important;
}

/* Unselected state - more transparent and subtle */
.badge-unselected {
    opacity: 0.5;
    font-weight: 400;
    z-index: 1 !important;
}

.badge-unselected:hover {
    opacity: 0.8;
}

/* Button group styling for mode toggle */
.btn-group-sm .btn {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
}

.btn-group-sm .btn i {
    font-size: 0.875rem;
    margin-right: 0.25rem;
}

/* Tab navigation styling */
.tab-navigation .nav-tabs {
    border-bottom: 2px solid #e9ecef;
}

.tab-navigation .nav-link {
    border: none;
    background: transparent;
    color: #6c757d;
    font-weight: 500;
    padding: 0.75rem 1.5rem;
    border-radius: 0;
    border-bottom: 3px solid transparent;
    transition: all 0.3s ease;
}

.tab-navigation .nav-link:hover {
    background: rgba(0, 123, 255, 0.05);
    color: #0056b3;
    border-bottom-color: rgba(0, 123, 255, 0.3);
}

.tab-navigation .nav-link.active {
    background: transparent;
    color: #0056b3;
    border-bottom-color: #0056b3;
    font-weight: 600;
}

.tab-navigation .nav-link i {
    font-size: 1rem;
}

.tab-navigation .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    position: relative;
    right: auto;
}

.tab-navigation .badge-light {
    background-color: rgba(0, 123, 255, 0.1);
    color: #0056b3;
}

.tab-navigation .badge-secondary {
    background-color: #f8f9fa;
    color: #6c757d;
}

/* Selection badge styling */
.tab-navigation .badge-success {
    background-color: #28a745;
    color: white;
    font-size: 0.65rem;
    padding: 0.2rem 0.4rem;
    border-radius: 10px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    box-shadow: 0 1px 3px rgba(40, 167, 69, 0.3);
    animation: badgeAppear 0.3s ease-out;
}

.tab-navigation .badge-success i {
    font-size: 0.6rem;
    margin-right: 0.2rem;
}

/* Badge appearance animation */
@keyframes badgeAppear {
    0% {
        opacity: 0;
        transform: scale(0.8);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

/* Tab content styling */
.tab-content {
    margin-top: 1rem;
}

/* Current car highlighting */
.current-car-highlight {
    border: 2px solid #1976d2 !important;
    position: relative;
    overflow: visible;
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
    /* Réserve la place du ruban qui flotte au-dessus de la carte (évite qu'il recouvre le nom de la voiture) */
    margin-top: 14px;
}

.current-car-indicator {
    position: absolute;
    /* Ruban positionné entièrement au-dessus de la carte : ne chevauche plus le titre de la voiture */
    top: auto;
    bottom: calc(100% + 7px);
    right: 12px;
    background: linear-gradient(135deg, #1976d2, #42a5f5);
    color: white;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
    display: flex;
    align-items: center;
    white-space: nowrap;
}

.current-car-indicator i {
    color: #ffd700;
    font-size: 0.8rem;
}

/* Mode indicator positioning for tabs */
.mode-indicator {
    position: absolute;
    bottom: 40px;
    right: 0px;
}

.mode-indicator .badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.375rem;
}

@media screen and (max-width: 450px) {
    .mode-selectors {
        bottom: 16px;
        left: 53px;
    }

    .mode-selectors .badge {
        scale: 1.3;
    }

    .current-car-indicator {
        top: auto;
        bottom: calc(100% + 7px);
        left: 10px;
        right: auto;
        padding: 3px 8px;
        font-size: 0.7rem;
    }
}
</style>
